import io from 'socket.io-client';
import { SERVER_URL } from '../config';

// Socket.IO connection
let socket = null;
let registeredSocketId = null;
let registeredUserId = null;
let activeRegister = { uid: null, userName: 'Participant' };
let pendingRoom = null;

// Latest remote-user state setter — updated each time initSocket is called so
// event handlers always use the current React state setter even after re-renders.
let latestSetRemoteUsers = null;

const emitJoinRoom = () => {
  if (!socket || !socket.connected || !pendingRoom) return;
  socket.emit('join-room', {
    channelName: pendingRoom.channelName,
    uid: String(pendingRoom.uid),
    userName: pendingRoom.userName
  });
};

const registerCurrentSocket = () => {
  if (!socket || !socket.connected || !activeRegister.uid) return;
  if (registeredSocketId === socket.id && registeredUserId === String(activeRegister.uid)) return;
  socket.emit('register', {
    userId: String(activeRegister.uid),
    userName: activeRegister.userName || 'Participant'
  });
  registeredSocketId = socket.id;
  registeredUserId = String(activeRegister.uid);
  console.log('Registered user with UID:', activeRegister.uid);
};

export const registerSocketUser = (uid, userName = 'Participant') => {
  if (!uid) return;
  activeRegister = { uid: String(uid), userName: userName || 'Participant' };
  registerCurrentSocket();
};

// Initialize Socket.IO connection
export const initSocket = (setRemoteUsersState, uid = null, userName = 'Participant') => {
  if (uid) activeRegister = { uid: String(uid), userName };

  // Always update the latest setter so reconnect handlers use current closure.
  if (setRemoteUsersState) latestSetRemoteUsers = setRemoteUsersState;

  if (socket) return socket;

  console.log('🔌 Connecting to backend server:', SERVER_URL);
  socket = io(SERVER_URL, {
    // Try WebSocket first — polling adds 200-400ms latency and drains battery on mobile.
    // Falls back to polling automatically if WebSocket is blocked by a proxy.
    transports: ['websocket', 'polling'],
    // Never give up reconnecting. Mobile networks switch between WiFi/LTE frequently;
    // losing 6+ consecutive pings should not permanently kill the socket.
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
    forceNew: true
  });

  socket.on('connect', () => {
    console.log('✅ Connected to backend server');
    registeredSocketId = null;
    registeredUserId = null;
    registerCurrentSocket();
    emitJoinRoom();
  });

  socket.on('connect_error', (error) => {
    console.error('❌ Socket connection error:', error);
  });

  socket.on('disconnect', () => {
    console.log('❌ Disconnected from backend server');
    registeredSocketId = null;
    registeredUserId = null;
  });

  socket.on('room-joined', (data) => {
    console.log('📞 Successfully joined room:', data);
  });

  socket.on('join-error', (error) => {
    console.error('❌ Failed to join room:', error);
  });

  // All remote-user state events go through latestSetRemoteUsers so they
  // always call the current React setter even after re-renders.
  socket.on('user-joined', (data) => {
    console.log('👤 New user joined:', data);
    if (!latestSetRemoteUsers) return;
    latestSetRemoteUsers(prev => ({
      ...prev,
      [data.uid]: {
        muted: false,
        videoOff: false,
        ...(prev[data.uid] || {}),
        uid: data.uid,
        userName: data.userName
      }
    }));
  });

  socket.on('user-left', (data) => {
    console.log('👋 User left:', data);
    if (!latestSetRemoteUsers) return;
    latestSetRemoteUsers(prev => {
      const next = { ...prev };
      delete next[data.uid];
      return next;
    });
  });

  socket.on('user-muted', (data) => {
    console.log('🔇 Remote user mute changed:', data);
    if (!latestSetRemoteUsers) return;
    latestSetRemoteUsers(prev => ({
      ...prev,
      [data.uid]: { ...prev[data.uid], muted: data.muted }
    }));
  });

  socket.on('user-video-off', (data) => {
    console.log('📹 Remote user video changed:', data);
    if (!latestSetRemoteUsers) return;
    latestSetRemoteUsers(prev => ({
      ...prev,
      [data.uid]: { ...prev[data.uid], videoOff: data.videoOff }
    }));
  });

  socket.on('prompt-register', (data) => {
    console.log('Server prompted registration:', data);
    registerCurrentSocket();
  });

  return socket;
};

// Join room via socket
export const joinRoom = (channelName, uid, userName = 'Anonymous') => {
  if (!channelName || !uid) return;
  pendingRoom = { channelName, uid: String(uid), userName };
  registerSocketUser(uid, userName);

  if (socket && socket.connected) {
    console.log('Emitting join-room:', { channelName, uid });
    emitJoinRoom();
  }
};

// Leave room via socket
export const leaveRoom = (channelName, uid) => {
  if (pendingRoom?.channelName === channelName && String(pendingRoom.uid) === String(uid)) {
    pendingRoom = null;
  }
  if (socket && channelName) {
    socket.emit('leave-room', { channelName, uid: String(uid) });
  }
};

// Toggle mute via socket
export const toggleMuteSocket = (muted, channelName, uid) => {
  if (socket && channelName) {
    socket.emit('toggle-mute', { muted, channelName, uid: uid ? String(uid) : undefined });
  }
};

// Toggle video via socket
export const toggleVideoSocket = (videoOff, channelName, uid) => {
  if (socket && channelName) {
    socket.emit('toggle-video', { videoOff, channelName, uid: uid ? String(uid) : undefined });
  }
};

// Disconnect socket
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    registeredSocketId = null;
    registeredUserId = null;
    pendingRoom = null;
    latestSetRemoteUsers = null;
  }
};

// Check if socket is connected
export const isSocketConnected = () => {
  return socket && socket.connected;
};

// Send chat message
export const sendChatMessage = (data) => {
  if (socket && socket.connected) {
    socket.emit('chat-message', {
      channelName: data.channelName,
      message: data.message,
      senderId: data.senderId,
      senderName: data.senderName
    });
  }
};

// Listen for chat messages — returns a cleanup function that removes only this listener.
export const onChatMessage = (callback) => {
  if (!socket) return () => {};
  const handler = (data) => callback(data);
  socket.on('chat-message', handler);
  return () => {
    socket.off('chat-message', handler);
  };
};

// Send vitals update
export const sendVitalsUpdate = (data) => {
  if (socket && socket.connected) {
    socket.emit('vitals-update', {
      channelName: data.channelName,
      patientId: data.patientId,
      vitals: data.vitals
    });
  }
};

// Listen for vitals updates
export const onVitalsUpdate = (callback) => {
  if (socket) {
    socket.on('vitals-update', (data) => {
      callback(data);
    });
    return () => {
      socket.off('vitals-update');
    };
  }
  return () => {};
};

// Call ended event
export const onCallEnded = (callback) => {
  if (!socket) return () => {};
  const handler = (data) => callback(data);
  socket.on('call-ended', handler);
  return () => {
    socket.off('call-ended', handler);
  };
};
